import { FC, useState, useEffect } from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  VStack,
  Text,
  useColorModeValue,
  Spinner,
  Center
} from '@chakra-ui/react';
import { MdLibraryBooks, MdQuiz, MdVideoLibrary, MdAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { getCourses, Course } from '../../services/university-service';
import { PageHeading, Card } from '@bemap/ui';

const UniversityPage: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const cardBg = useColorModeValue('white', 'gray.800');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  // Load courses from API
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        const response = await getCourses();
        if (response.statusCode === 200) {
          setCourses(response.message || []);
        }
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  // Calculate statistics from real data
  const stats = {
    totalCourses: courses.length,
    totalStudents: courses.reduce((sum, course) => sum + (course.modules?.length || 0), 0),
    totalContent: courses.reduce((sum, course) => {
      const moduleContents = course.modules?.reduce((moduleSum, module) =>
        moduleSum + (module.contents?.length || 0), 0) || 0;
      return sum + moduleContents;
    }, 0),
    activeQuizzes: courses.reduce((sum, course) => {
      const quizContent = course.modules?.reduce((moduleSum, module) =>
        moduleSum + (module.contents?.filter(content => content.type === 'quiz').length || 0), 0) || 0;
      return sum + quizContent;
    }, 0),
    videoContent: courses.reduce((sum, course) => {
      const videoContent = course.modules?.reduce((moduleSum, module) =>
        moduleSum + (module.contents?.filter(content => content.type === 'video').length || 0), 0) || 0;
      return sum + videoContent;
    }, 0),
    documentContent: courses.reduce((sum, course) => {
      const documentContent = course.modules?.reduce((moduleSum, module) =>
        moduleSum + (module.contents?.filter(content => content.type === 'document').length || 0), 0) || 0;
      return sum + documentContent;
    }, 0)
  };

  const handleCreateCourse = () => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_CREATE);
  };

  const handleViewCourses = () => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={t('university.dashboard.title')} actions={[
        {
          label: t('university.dashboard.create_course'),
          onClick: handleCreateCourse,
          icon: <Icon as={MdAdd} />
        },
        {
          variant: 'outline',
          label: t('university.dashboard.view_all_courses'),
          onClick: handleViewCourses,
          icon: <Icon as={MdLibraryBooks} />
        }
      ]} />

      {/* Statistics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        <Card bg={cardBg}>
          <Stat>
            <StatLabel>{t('university.dashboard.total_courses')}</StatLabel>
            <StatNumber color="blue.600">{stats.totalCourses}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              12% {t('university.dashboard.from_last_month')}
            </StatHelpText>
          </Stat>
        </Card>

        <Card bg={cardBg}>
          <Stat>
            <StatLabel>{t('university.dashboard.active_students')}</StatLabel>
            <StatNumber color="green.600">{stats.totalStudents}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              8% {t('university.dashboard.from_last_month')}
            </StatHelpText>
          </Stat>
        </Card>

        <Card bg={cardBg}>
          <Stat>
            <StatLabel>{t('university.dashboard.total_content_items')}</StatLabel>
            <StatNumber color="purple.600">{stats.totalContent}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              15% {t('university.dashboard.from_last_month')}
            </StatHelpText>
          </Stat>
        </Card>
      </SimpleGrid>

      {/* Content Type Statistics */}
      <Card bg={cardBg}>
        <Heading size="md" mb={4}>{t('university.dashboard.content_distribution')}</Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Box p={4} bg={statBg} borderRadius="md" textAlign="center">
            <Icon as={MdVideoLibrary} boxSize={8} color="red.500" mb={2} />
            <Text fontSize="2xl" fontWeight="bold" color="red.600">
              {stats.videoContent}
            </Text>
            <Text fontSize="sm" color="gray.600">{t('university.dashboard.video_content')}</Text>
          </Box>

          <Box p={4} bg={statBg} borderRadius="md" textAlign="center">
            <Icon as={MdLibraryBooks} boxSize={8} color="blue.500" mb={2} />
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {stats.documentContent}
            </Text>
            <Text fontSize="sm" color="gray.600">{t('university.dashboard.documents')}</Text>
          </Box>

          <Box p={4} bg={statBg} borderRadius="md" textAlign="center">
            <Icon as={MdQuiz} boxSize={8} color="green.500" mb={2} />
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {stats.activeQuizzes}
            </Text>
            <Text fontSize="sm" color="gray.600">{t('university.dashboard.active_quizzes')}</Text>
          </Box>
        </SimpleGrid>
      </Card>
    </VStack>
  );
};

export default UniversityPage; 